import {
  completion,
  completionStream,
  createNewChannel,
  fetchChannelDetail,
  fetchChannelList,
} from "@/http/chat";
import {
  Channel,
  ChannelHistory,
  ServerMessageElement,
  StreamChunk,
  ChatSource,
} from "@/types/chat";
import { string } from "zod";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { nanoid } from "nanoid";
import dayjs from "dayjs";
import {
  ChatMessage,
  createMessage,
  getMessageListByHistory,
  AnswerReference,
} from "@/lib/formatter/chat";
import { paths } from "@/paths";

/**
 * runtime 维护的页面 state，区别于从网络请求获得的数据
 */

export type ChatSession = {
  id: number | string;
  topic: string;
  messages: ChatMessage[];
  createTime: string | Date;
};

interface ChatStore {
  sessions: ChatSession[];
  getSessions: () => ChatSession[];
  currentSessionId: string | number | null;
  getCurrentSessionId: () => string | number | null;
  uiState: {
    isHistoryVisible: boolean;
    isReferenceVisible: boolean;
    isSessionsLoading: boolean;
    sessionLoadError: string | null;
    isMessagesLoading: boolean;
    isSelectionMode: boolean;
    chatRefMode: "index" | "detail";
    isDeepAnalysis: boolean;
    isInitialized: boolean;
  };
  selectedMessages: Set<string>;
  currentReferences: AnswerReference[];
  loadedSessionIds: Set<string>;
  chatRefSources: ChatSource[];
  setSessions: (sessions: ChatSession[]) => void;
  selectSession: (id: string | number | null) => void;
  onUserInput: (
    sessionId: number | string | null | undefined,
    paylod: {
      query: string;
      newSessionName?: string; // 创建新频道的名称
      newSessionType?: "filings" | "copilot"; // 创建新频道的类型
      fileUrls?: string[];
      onNewSessionCallback?: (sessionId: number | string) => void;
    }
  ) => Promise<number | string | void>;
  updateTargetSession: (id: number | string, updater: (session: ChatSession) => void) => void;
  loadMessagesForSession: (sessionId: string | number) => Promise<void>;
  flushTargetSession: (id: number | string) => void;
  initializeSessions: () => Promise<void>;
  clearSessions: () => void;
  getMessages: (sessionId?: number | string) => ChatMessage[];
  setUIState: (state: Partial<ChatStore["uiState"]>) => void;
  setCurrentReferences: (references: AnswerReference[]) => void;
  setChatRefSources: (sources: ChatSource[]) => void;
  clearMessageCache: (sessionId?: string | number) => void;
  toggleSelectionMode: () => void;
  toggleMessageSelection: (messageNanoId: string) => void;
  selectAllMessages: (sessionId: string | number) => void;
  clearSelection: () => void;
  convertSelectedMessagesToShareList: () => string[];
  abortCurrentResponse: (sessionId: string | number) => void;
}

// 模拟的测试数据
const mockSessions = [
  {
    id: "1",
    topic: "分析贵州茅台最新财报",
    createTime: "2024-03-20T10:00:00Z",
    messages: [],
  },
  {
    id: "2",
    topic: "比较中国平安和中国人寿的估值",
    createTime: "2024-03-19T15:30:00Z",
    messages: [],
  },
  {
    id: "3",
    topic: "分析新能源汽车行业趋势",
    createTime: "2024-03-18T09:15:00Z",
    messages: [],
  },
  {
    id: "4",
    topic: "解读最新的 A 股市场政策",
    createTime: "2024-03-17T14:20:00Z",
    messages: [],
  },
];

export const useChatStore = create<ChatStore>()(
  devtools(
    (set, getState) => ({
      sessions: [],
      currentSessionId: null,
      loadedSessionIds: new Set<string>(),
      uiState: {
        isHistoryVisible: true,
        isReferenceVisible: false,
        isSessionsLoading: false,
        sessionLoadError: null,
        isMessagesLoading: false,
        isSelectionMode: false,
        chatRefMode: "index",
        isDeepAnalysis: false,
        isInitialized: false,
      },
      currentReferences: [],
      selectedMessages: new Set<string>(),
      chatRefSources: [],

      setSessions: (sessions: ChatSession[]) => {
        set(() => ({
          sessions: sessions,
        }));
      },

      getSessions: () => getState().sessions,

      selectSession: (id: string | number | null) => {
        set((state) => ({
          currentSessionId: id,
          uiState: {
            ...state.uiState,
            isSelectionMode: false,
            isReferenceVisible: false,
          },
        }));
      },

      getMessages(sessionId?: number | string) {
        const session = getState().sessions.find((s) => s.id?.toString() === sessionId?.toString());
        return session ? session.messages : [];
      },

      getCurrentSessionId: () => getState().currentSessionId,

      async onUserInput(sessionId: number | string | undefined | null, payload) {
        const { query, newSessionName, newSessionType, fileUrls, onNewSessionCallback } = payload;

        // 如果没有 sessionId，说明是新对话
        if (!sessionId) {
          try {
            // 直接创建服务器会话
            const channel = await createNewChannel({
              name: newSessionName || getDefaultChannelName(query),
              type: newSessionType,
            });

            // 添加新会话到 store
            const newSession: ChatSession = {
              id: channel.id,
              topic: getDefaultChannelName(query),
              messages: [],
              createTime: new Date(),
            };

            set((state) => ({
              sessions: [...state.sessions, newSession],
              currentSessionId: channel.id,
            }));

            // 更新 url
            window.history.replaceState({}, "", `${paths.console.copilot}/${channel.id}`);

            // 发送请求获取回复
            const userMessage: ChatMessage = createMessage({
              role: "user",
              content: query,
            });

            const serverMessage: ChatMessage = createMessage({
              role: "server",
              loading: true,
              stream: true,
              streamDone: false,
              content: [],
            });

            // 保存用户消息和机器人消息
            getState().updateTargetSession(channel.id, (session) => {
              session.messages = session.messages.concat([userMessage, serverMessage]);
            });

            try {
              await completionStream(
                channel.id,
                query,
                (chunk) => {
                  serverMessage.loading = false;
                  serverMessage.content = [...(serverMessage.content as StreamChunk[]), chunk];
                  getState().flushTargetSession(channel.id);
                },
                getState().uiState.isDeepAnalysis,
                fileUrls,
                () => {
                  console.log("stream done");
                  serverMessage.streamDone = true;
                  getState().flushTargetSession(channel.id);

                  // 添加新会话到 loadedSessionIds
                  set((state) => ({
                    loadedSessionIds: new Set([...state.loadedSessionIds, channel.id.toString()]),
                  }));

                  // stream 完成后触发回调
                  if (onNewSessionCallback) {
                    onNewSessionCallback(channel.id);
                  }
                }
              );

              serverMessage.updateTime = new Date();
              getState().flushTargetSession(channel.id);
              return channel.id;
            } catch (error) {
              serverMessage.loading = false;
              serverMessage.isError = true;
              serverMessage.content = [];
              getState().flushTargetSession(channel.id);
              throw error;
            }
          } catch (error) {
            console.error("创建会话失败:", error);
            throw error;
          }
        } else {
          // 已存在的会话直接发送请求
          const currentSession = getState().sessions.find(
            (v) => v.id.toString() === sessionId.toString()
          );
          if (!currentSession) return;

          const userMessage: ChatMessage = createMessage({
            role: "user",
            content: query,
          });

          const serverMessage: ChatMessage = createMessage({
            role: "server",
            loading: true,
            stream: true,
            streamDone: false,
            content: [],
          });

          // 保存用户消息和机器人消息
          getState().updateTargetSession(sessionId, (session) => {
            session.messages = session.messages.concat([userMessage, serverMessage]);
          });

          try {
            // 清除当前会话的缓存标记，因为即将产生新的对话历史
            getState().clearMessageCache(sessionId);
            await completionStream(
              currentSession.id,
              query,
              (chunk) => {
                serverMessage.loading = false;
                serverMessage.content = [...(serverMessage.content as StreamChunk[]), chunk];
                getState().flushTargetSession(currentSession.id);
              },
              getState().uiState.isDeepAnalysis,
              fileUrls,
              () => {
                console.log("stream done");
                serverMessage.streamDone = true;
                getState().flushTargetSession(currentSession.id);
              }
            );
            serverMessage.updateTime = new Date();
            getState().flushTargetSession(currentSession.id);
          } catch (error) {
            serverMessage.loading = false;
            serverMessage.isError = true;
            serverMessage.content = [];
            getState().flushTargetSession(currentSession.id);
          }
        }
      },

      loadMessagesForSession: async (sessionId: number | string) => {
        const state = getState();
        const sessionIdStr = sessionId.toString();
        if (state.loadedSessionIds.has(sessionIdStr)) {
          return;
        }
        set((state) => {
          return {
            uiState: {
              ...state.uiState,
              isMessagesLoading: true,
            },
          };
        });

        try {
          const res = await fetchChannelDetail({ channelId: +sessionId });
          const messages: ChatMessage[] = getMessageListByHistory(res.chat_history);

          // 更新消息时，保持 loading 状态为 true
          getState().updateTargetSession(sessionId, (session) => {
            session.messages = messages;
          });

          // 标记该会话消息已加载
          set((state) => {
            return {
              loadedSessionIds: new Set([...state.loadedSessionIds, sessionIdStr]),
              uiState: {
                ...state.uiState,
                isMessagesLoading: false,
              },
            };
          });
        } catch (error) {
          console.error("对话加载失败:", error);
          set((state) => ({
            uiState: {
              ...state.uiState,
              isMessagesLoading: false,
            },
          }));
        }
      },

      updateTargetSession(id: number | string, updater: (session: ChatSession) => void) {
        const sessions = getState().sessions;
        const index = sessions.findIndex((s) => s.id.toString() === id.toString());
        if (index < 0) return;
        updater(sessions[index]);
        set(() => ({ sessions: [...sessions] }));
      },

      flushTargetSession(id: number | string) {
        getState().updateTargetSession(id, (session) => {
          session.messages = session.messages.concat();
        });
      },

      initializeSessions: async () => {
        if (getState().uiState.isInitialized) {
          console.log("[initializeSessions] Store already initialized, skipping");
          return;
        }

        console.log("[initializeSessions] Starting initialization");
        set((state) => ({
          uiState: {
            ...state.uiState,
            isSessionsLoading: true,
            sessionLoadError: null,
          },
        }));

        try {
          const res = await fetchChannelList({});

          if (!res || !Array.isArray(res.data_list)) {
            throw new Error("Load session list failed");
          }

          const sessions = getSessionsByServer(res.data_list);

          set((state) => {
            return {
              sessions,
              uiState: {
                ...state.uiState,
                isSessionsLoading: false,
                sessionLoadError: null,
                isInitialized: true,
              },
            };
          });
        } catch (error) {
          set((state) => ({
            uiState: {
              ...state.uiState,
              isSessionsLoading: false,
              sessionLoadError: error instanceof Error ? error.message : "Load session list failed",
            },
          }));
        }
      },

      clearSessions: () =>
        set({
          sessions: [],
          currentSessionId: null,
          loadedSessionIds: new Set(), // 清除会话时同时清除缓存
        }),

      setUIState: (state) => {
        set((prev) => ({
          uiState: {
            ...prev.uiState,
            ...state,
          },
        }));
      },

      setCurrentReferences: (references) => {
        set({ currentReferences: references });
      },

      setChatRefSources: (sources) => {
        set({ chatRefSources: sources });
      },

      clearMessageCache: (sessionId?: string | number) => {
        if (sessionId) {
          // 清除指定会话的缓存
          set((state) => ({
            loadedSessionIds: new Set(
              Array.from(state.loadedSessionIds).filter((id) => id !== sessionId.toString())
            ),
          }));
        } else {
          // 清除所有缓存
          set({ loadedSessionIds: new Set() });
        }
      },

      toggleSelectionMode: () => {
        set((state) => ({
          uiState: {
            ...state.uiState,
            isSelectionMode: !state.uiState.isSelectionMode,
          },
        }));
      },

      toggleMessageSelection: (messageNanoId: string) => {
        const selectedMessages = getState().selectedMessages;
        if (selectedMessages.has(messageNanoId)) {
          selectedMessages.delete(messageNanoId);
        } else {
          selectedMessages.add(messageNanoId);
        }
        set(() => ({ selectedMessages: new Set(selectedMessages) }));
      },

      selectAllMessages: (sessionId: string | number) => {
        const session = getState().sessions.find((s) => s.id.toString() === sessionId.toString());
        if (session) {
          session.messages.forEach((message) => {
            getState().toggleMessageSelection(message.id);
          });
        }
      },

      clearSelection: () => {
        set(() => ({ selectedMessages: new Set<string>() }));
      },

      convertSelectedMessagesToShareList: () => {
        const state = getState();
        const currentMessages = state.getMessages(state.currentSessionId || undefined);
        return convertToShareList(state.selectedMessages, currentMessages);
      },

      abortCurrentResponse: (sessionId: string | number) => {
        const state = getState();
        const session = state.sessions.find((s) => s.id.toString() === sessionId.toString());

        if (!session) return;

        // 找到最后一条 server message
        const lastServerMessageIndex = session.messages.map((m) => m.role).lastIndexOf("server");
        if (lastServerMessageIndex === -1) return;

        const lastServerMessage = session.messages[lastServerMessageIndex];

        // 如果消息正在加载中，则移除该消息
        // 非流式响应可用 loading 判断，流式响应需要用 streamDone 判断
        if (lastServerMessage.loading || !lastServerMessage.streamDone) {
          state.updateTargetSession(sessionId, (session) => {
            session.messages = session.messages.filter(
              (_, index) => index !== lastServerMessageIndex
            );
          });
          getState().flushTargetSession(sessionId);
        }
      },
    }),
    { name: "ChatStore" }
  )
);

export function getSessionsByServer(channels: Channel[]): ChatSession[] {
  return channels.map((item) => {
    return {
      id: item.id,
      topic: item.name,
      createTime: item.createtime,
      messages: [],
    };
  });
}

function getDefaultChannelName(query: string): string {
  return query;
}

/**
 * 将选中的消息 ID 转换为分享列表格式
 * @param selectedNanoIds - 选中的消息 nanoId 集合
 * @param messages - 当前会话的所有消息
 * @returns 格式化的分享列表，如 ['113Q', '114QA', '115A']
 */
function convertToShareList(selectedNanoIds: Set<string>, messages: ChatMessage[]): string[] {
  // 1. 获取选中消息的完整信息
  const selectedMessages = messages.filter((msg) => selectedNanoIds.has(msg.id));

  // 2. 按 historyId 分组
  const groupedByHistory = new Map<number, { user: boolean; server: boolean }>();

  selectedMessages.forEach((msg) => {
    if (!msg.historyId) return; // 跳过没有 historyId 的消息

    const current = groupedByHistory.get(msg.historyId) || {
      user: false,
      server: false,
    };
    if (msg.role === "user") {
      current.user = true;
    } else {
      current.server = true;
    }
    groupedByHistory.set(msg.historyId, current);
  });

  // 3. 转换为分享列表格式
  const shareList: string[] = [];

  // 按 historyId 排序
  const sortedHistoryIds = Array.from(groupedByHistory.keys()).sort((a, b) => a - b);

  sortedHistoryIds.forEach((historyId) => {
    const { user, server } = groupedByHistory.get(historyId)!;
    let flag = "";

    if (user && server) {
      flag = "QA";
    } else if (user) {
      flag = "Q";
    } else if (server) {
      flag = "A";
    }

    shareList.push(`${historyId}${flag}`);
  });

  return shareList;
}
